import { format, startOfMonth, endOfMonth } from "date-fns";
import { useMemo, useState, useEffect } from "react";
import { DATE_FORMATS } from "@/config";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useAvailableTimeSlotsLazyQuery } from "@/graphql/queries/availableTimeSlots.graphql.types";
import { TimeSlot } from "@/types/bookingTypes";

export default function useBookingDateTimeSelection() {
  const { selectedServices, selectedProvider, date, setTimeSlot } =
    useClientBookingContext();
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(date));
  const [accumulatedTimeSlots, setAccumulatedTimeSlots] = useState({});

  const visitDuration = useMemo(() => {
    return selectedServices.reduce(
      (acc, service) => acc + (service.durationInMinutes || 0),
      0
    );
  }, [selectedServices]);

  const [fetchTimeSlots, { loading }] = useAvailableTimeSlotsLazyQuery();

  useEffect(() => {
    const fetchTimeSlotsAsync = async () => {
      const startDate = format(currentMonth, DATE_FORMATS.SERVER_DATE);
      const endDate = format(
        endOfMonth(currentMonth),
        DATE_FORMATS.SERVER_DATE
      );
      try {
        const result = await fetchTimeSlots({
          variables: {
            providerId: selectedProvider?.id,
            durationInMinutes: visitDuration,
            startDate,
            endDate,
            serviceMenuItemIds: selectedServices.map((service) => service.id),
          },
          fetchPolicy: "network-only",
        });

        if (result.data) {
          setAccumulatedTimeSlots((prevTimeSlots) => {
            const currentTimeSlotsByProvider =
              prevTimeSlots[selectedProvider?.id] || [];
            const newTimeSlots = result.data.availableTimeSlots.dates.filter(
              (date) =>
                !currentTimeSlotsByProvider.some(
                  (existingDate) => existingDate.date === date.date
                )
            );
            return {
              ...prevTimeSlots,
              [selectedProvider?.id]: [
                ...currentTimeSlotsByProvider,
                ...newTimeSlots,
              ],
            };
          });
        }
      } catch (error) {
        console.error("Error fetching time slots:", error);
      }
    };

    setTimeSlot(null);

    fetchTimeSlotsAsync();
  }, [
    currentMonth,
    fetchTimeSlots,
    selectedProvider?.id,
    visitDuration,
    selectedServices,
    setTimeSlot,
  ]);

  const accumulatedTimeSlotsByProvider = useMemo(() => {
    return accumulatedTimeSlots[selectedProvider?.id] || [];
  }, [accumulatedTimeSlots, selectedProvider?.id]);

  const datesWithTimeSlots = useMemo(() => {
    return accumulatedTimeSlotsByProvider.filter(
      (date) => date.slots.length > 0
    );
  }, [accumulatedTimeSlotsByProvider]);

  const bookingAvailableDates = useMemo(() => {
    return datesWithTimeSlots.map((date) => date.date);
  }, [datesWithTimeSlots]);

  const timeSlotsForSelectedDate = useMemo(() => {
    const selectedDateStr = format(date, DATE_FORMATS.SERVER_DATE);
    const selectedDate = datesWithTimeSlots.find(
      (d) => d.date === selectedDateStr
    );
    return selectedDate
      ? selectedDate.slots.map((slot) => ({
          endStr: slot.end,
          startStr: slot.start,
        }))
      : [];
  }, [date, datesWithTimeSlots]);

  const handleTimeSlotSelection = (timeSlot: TimeSlot) => {
    setTimeSlot(timeSlot);
  };

  const handleMonthChange = (newMonthDate: Date) => {
    setCurrentMonth(startOfMonth(newMonthDate));
  };

  return {
    bookingAvailableDates,
    timeSlotsForSelectedDate,
    handleTimeSlotSelection,
    handleMonthChange,
    loading,
  };
}
